import type { Notification } from '@awork/framework/models/notification.model'
import type { Project } from '@awork/features/project/models/project.model'

export type ValidEntityTypes =
  'projects' | 'tasks' | 'timetrackings' | 'documents' | 'documentspaces' | 'filters' | 'agents'
export type GroupingType = 'event-entity-user' | 'event-entity' | 'event-user' | 'event-reminder' | 'no-group'
export type NotificationType = 'event' | 'reminder'
export type NotificationGroupType = 'conversations' | 'eventNotifications'
export type ConversationType = 'unseen' | 'seen'
export type ProjectGroupType = 'projectUpdates'
export type TaskGroupType = 'newTasks' | 'unassignedTasks' | 'taskPredecessorDone' | 'privateTaskUpdates'
export type DocumentGroupType = 'newDocuments'
export type DocumentSpaceGroupType = 'newDocumentSpaces'
export type FilterGroupType = 'newFilters'
export type AgentGroupType = 'newSharedAgents' | 'newSharedThreads'
export type EventNotificationType =
  | 'newProjects'
  | 'deadlines'
  | 'birthdays'
  | 'reminders'
  | 'default'
  | TaskGroupType
  | DocumentGroupType
  | DocumentSpaceGroupType
  | FilterGroupType
  | AgentGroupType
export type GroupNameByParentEntity = 'project' | 'documentSpace' | 'privateDocument' | 'privateTask' | undefined

export interface SnoozeNotification {
  notification: Notification
  offset: RemindMeOffsets
}

export interface ConversationsGroup<Entity = undefined> {
  entityName?: string
  entity?: Entity
  notifications: Notification[]
  seen?: boolean
}

export interface ProjectUpdateGroup {
  project: Project
  updatedStatus?: Notification[]
  seen?: boolean
}

export interface NotificationsGroup {
  groupType: EventNotificationType | ConversationType
  groups: Notification[]
}

export interface ProjectNotificationsGroup {
  groupType: ProjectGroupType
  groups: ProjectUpdateGroup[]
}

export interface TaskNotificationsGroup {
  groupType: TaskGroupType
  groups: Notification[]
}

export interface DocumentNotificationsGroup {
  groupType: DocumentGroupType
  groups: Notification[]
}

export interface DocumentSpaceNotificationsGroup {
  groupType: DocumentSpaceGroupType
  groups: Notification[]
}

export interface FilterNotificationsGroup {
  groupType: FilterGroupType
  groups: Notification[]
}

export type NotificationGroup =
  | NotificationsGroup
  | ProjectNotificationsGroup
  | TaskNotificationsGroup
  | DocumentNotificationsGroup
  | DocumentSpaceNotificationsGroup
  | FilterNotificationsGroup

export type NotificationGroups = (
  | NotificationsGroup
  | ProjectNotificationsGroup
  | TaskNotificationsGroup
  | DocumentNotificationsGroup
  | DocumentSpaceNotificationsGroup
  | FilterNotificationsGroup
)[]

export interface GroupedNotifications {
  conversations: NotificationGroup[]
  eventNotifications: NotificationGroup[]
}

export enum NotificationEvents {
  TaskStatusChanged = 'task_status_changed',
  TaskAssigned = 'task_assigned',
  TaskUnassigned = 'task_unassigned',
  TaskCommented = 'task_commented',
  TaskCommentReacted = 'task_comment_reacted',
  TaskDue = 'task_due',
  ProjectStatusChanged = 'project_status_changed',
  ProjectAssigned = 'project_assigned',
  ProjectAutoConnected = 'project_auto_connected',
  ProjectCommented = 'project_commented',
  ProjectCommentReacted = 'project_comment_reacted',
  ProjectDue = 'project_due',
  ProjectDueWeekend = 'project_due_weekend',
  WeeklyTimeEntriesReport = 'report_time_entries_week',
  MonthlyTimeEntriesReport = 'report_time_entries_month',
  Birthday = 'birthday',
  ExportAvailable = 'export_available',
  ExportFailed = 'export_failed',
  TaskScheduleSyncCompleted = 'task_schedule_sync_completed',
  BillingInvoiceTransferSucceeded = 'billing_invoice_transfer_succeeded',
  BillingInvoiceTransferNeedsAttention = 'billing_invoice_transfer_needs_attention',
  WeeklyDueTasksSummary = 'weekly_due_tasks_summary',
  TimeTrackingReminder = 'timetracking_reminder',
  ProjectMentioned = 'project_comment_mentioned',
  TaskMentioned = 'task_comment_mentioned',
  CommentMentioned = 'comment_mentioned',
  CommentReacted = 'comment_reacted',
  TaskPredecessorFinished = 'task_predecessor_finished',
  TasksImportedFinished = 'import_tasks_finished',
  PlannerAutofixComplete = 'autofix-complete',
  PlannerRefineComplete = 'refine-complete',
  DocumentCommented = 'document_commented',
  DocumentCommentReacted = 'document_comment_reacted',
  DocumentCommentMentioned = 'document_comment_mentioned',
  DocumentMentioned = 'document_mentioned',
  DocumentContributorAdded = 'document_contributor_added',
  DocumentSpaceContributorAdded = 'document_space_contributor_added',
  FilterContributorAdded = 'filter_contributor_added',
  AutomationDeactivated = 'automation_deactivated',
  AgentThreadAwaitingAction = 'agent_thread_awaiting_action',
  AgentThreadShared = 'agent_thread_shared',
  AgentMeetingSummarized = 'agent_meeting_summarized',
  MeetingRecordingBlocked = 'meeting_recording_blocked',
  AgentShared = 'agent_shared',
  AiCredits = 'ai_credits',
  AiCreditsLow = 'ai_credits_low',
  AiCreditsUsedUp = 'ai_credits_used_up',
  AiAgentRunBlocked = 'ai_agent_run_blocked',
  AiAgentRunBlockedAdmin = 'ai_agent_run_blocked_admin',
  AiPlanUpgraded = 'ai_plan_upgraded'
}

export enum RemindMeOffsets {
  OneHour = '1H',
  ThreeHours = '3H',
  Tomorrow = 'TOMORROW',
  NextWeek = 'NEXT_WEEK'
}

export const CONVERSATION_TYPES = [
  NotificationEvents.TaskCommented,
  NotificationEvents.TaskMentioned,
  NotificationEvents.ProjectCommented,
  NotificationEvents.ProjectMentioned,
  NotificationEvents.CommentReacted,
  NotificationEvents.TaskCommentReacted,
  NotificationEvents.ProjectCommentReacted,
  NotificationEvents.CommentMentioned,
  NotificationEvents.DocumentMentioned,
  NotificationEvents.DocumentCommented,
  NotificationEvents.DocumentCommentMentioned,
  NotificationEvents.DocumentCommentReacted
]
export const EVENT_NOTIFICATION_TYPES = [
  NotificationEvents.ProjectAssigned,
  NotificationEvents.ProjectAutoConnected,
  NotificationEvents.TaskAssigned,
  NotificationEvents.TaskStatusChanged,
  NotificationEvents.TaskUnassigned,
  NotificationEvents.TaskPredecessorFinished,
  NotificationEvents.TaskDue,
  NotificationEvents.ProjectDue,
  NotificationEvents.ProjectDueWeekend,
  NotificationEvents.Birthday,
  NotificationEvents.ExportAvailable,
  NotificationEvents.ExportFailed,
  NotificationEvents.TaskScheduleSyncCompleted,
  NotificationEvents.BillingInvoiceTransferSucceeded,
  NotificationEvents.BillingInvoiceTransferNeedsAttention,
  NotificationEvents.TasksImportedFinished,
  NotificationEvents.WeeklyTimeEntriesReport,
  NotificationEvents.MonthlyTimeEntriesReport,
  NotificationEvents.TaskStatusChanged,
  NotificationEvents.ProjectStatusChanged,
  NotificationEvents.PlannerAutofixComplete,
  NotificationEvents.PlannerRefineComplete,
  NotificationEvents.DocumentContributorAdded,
  NotificationEvents.DocumentSpaceContributorAdded,
  NotificationEvents.FilterContributorAdded,
  NotificationEvents.AutomationDeactivated,
  NotificationEvents.AgentThreadAwaitingAction,
  NotificationEvents.AgentThreadShared,
  NotificationEvents.AgentMeetingSummarized,
  NotificationEvents.MeetingRecordingBlocked,
  NotificationEvents.AgentShared,
  NotificationEvents.AiCreditsLow,
  NotificationEvents.AiCreditsUsedUp,
  NotificationEvents.AiAgentRunBlocked,
  NotificationEvents.AiAgentRunBlockedAdmin,
  NotificationEvents.AiPlanUpgraded
]
